import { useState, useEffect } from 'react'
import { AppMeta } from '@xnav/types/src/common'
import {
	IconButton,
	Modal,
	ModalOverlay,
	ModalContent,
	ModalHeader,
	ModalBody,
	ModalCloseButton,
	useColorModeValue,
	Box
} from '@chakra-ui/react'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { faPlus } from '@fortawesome/free-solid-svg-icons'
import { v4 } from 'uuid'
import AppForm from './appForm'

interface AddAppButtonProps {
	onValidate: (newApp: AppMeta) => string | undefined
	onAdd: (newApp: AppMeta, file: File | undefined) => void
	userRole?: string | null
	isOpen?: boolean
	onOpen?: () => void
	onClose?: () => void
	hideFab?: boolean
}

const AddAppButton: React.FC<AddAppButtonProps> = ({ 
	onAdd, 
	onValidate, 
	userRole,
	isOpen: externalIsOpen,
	onOpen: externalOnOpen,
	onClose: externalOnClose,
	hideFab = false
}) => {
	const [internalAddingApp, setInternalAddingApp] = useState(false)
	const isControlled = externalIsOpen !== undefined
	const isModalOpen = isControlled ? externalIsOpen : internalAddingApp

	const handleOpen = () => {
		if (isControlled && externalOnOpen) {
			externalOnOpen()
		} else {
			setInternalAddingApp(true)
		}
	}

	const handleClose = () => {
		if (isControlled && externalOnClose) {
			externalOnClose()
		} else {
			setInternalAddingApp(false)
		}
	}

	// Listen to global open new course event if triggered anywhere
	useEffect(() => {
		const handleOpenEvent = () => handleOpen();
		window.addEventListener('open-create-course-modal', handleOpenEvent);
		return () => window.removeEventListener('open-create-course-modal', handleOpenEvent);
	}, [isControlled, externalOnOpen]);

	// Default new app state
	const defaultApp: AppMeta = {
		name: '',
		id: v4(),
		description: '',
		size: 0,
		created: new Date().toString(),
		edited: new Date().toString(),
		courseType: 'Beginner',
		creator: ''
	}

	return (
		<>
			{/* Floating Action Button */}
			{!hideFab && (
				<IconButton
					position="fixed"
					bottom="36px"
					right="36px"
					zIndex={999}
					borderRadius="full"
					boxShadow="0 10px 30px rgba(14, 165, 233, 0.35)"
					bgGradient="linear(to-br, #38bdf8, #0ea5e9, #10b981)"
					color="white"
					size="lg"
					w="56px"
					h="56px"
					fontSize="20px"
					aria-label='Create New Course'
					icon={<FontAwesomeIcon icon={faPlus} />}
					onClick={handleOpen}
					_hover={{ transform: 'scale(1.1) rotate(90deg)', shadow: '0 12px 36px rgba(14, 165, 233, 0.5)' }}
					transition="all 0.3s cubic-bezier(0.4, 0, 0.2, 1)"
				/>
			)}

			{/* Add App Modal */}
			<Modal isOpen={isModalOpen} onClose={handleClose} size="xl" isCentered>
				<ModalOverlay backdropFilter="blur(8px)" bg="rgba(15, 23, 42, 0.4)" />
				<ModalContent borderRadius="2xl" border="1px solid" borderColor={useColorModeValue("purple.50", "gray.750")} bg={useColorModeValue("white", "gray.850")} shadow="2xl">
					<ModalHeader borderBottom="1px solid" borderColor={useColorModeValue("gray.100", "gray.750")} fontWeight="bold" color={useColorModeValue("gray.800", "white")}>
						Create New Course
					</ModalHeader>
					<ModalCloseButton borderRadius="full" m={2} />
					<ModalBody py={6}>
						<AppForm
							app={defaultApp}
							onAdd={(newApp, file) => {
								onAdd(newApp, file)
								handleClose()
							}}
							onCancel={handleClose}
							onValidate={onValidate}
							userRole={userRole}
						/>
					</ModalBody>
				</ModalContent>
			</Modal>
		</>
	)
}

export default AddAppButton
